import { useTheme } from '@/contexts/ThemeContext';
import { LOGO_INVERT_FILTER, resolveAppLogo } from '@/components/settings/branding/brandingHelpers';
import { useEffect, useState } from 'react';

interface LogoProps {
  className?: string;
  alt?: string;
  fallbackSrc?: string;
}

/**
 * The tenant logo in app chrome (Header, Navbar).
 *
 * This used to render the light logo through `dark:invert dark:hue-rotate-180` unconditionally.
 * The filter itself is sound for a solid-color mark (invert flips lightness, the hue rotation
 * puts the hue back); applying it to every logo was not, because anything with more than one
 * color came out in colors nobody chose.
 *
 * So it is now one of three choices the tenant makes on Settings › Branding, previewed there
 * against the real sidebar color: use the light logo as-is, invert it, or upload a dark
 * variant. `resolveAppLogo` owns that decision.
 */
export function Logo({
  className = 'h-8 w-auto',
  alt = 'Joules',
  fallbackSrc = '/joulescrmlogo.svg',
}: LogoProps) {
  const { currentTheme, themeSettings, isDarkMode } = useTheme();

  // `currentTheme.logo` is the resolved LIGHT logo (custom_logo, else the theme's own).
  // Keeping the dark treatment separate rather than folding it into currentTheme is deliberate:
  // proposals, share links and the customer portal all read currentTheme.logo and all render
  // on a light ground, so they must never receive the dark variant or the filter.
  const resolved = resolveAppLogo(
    currentTheme.logo,
    themeSettings.customLogoDark,
    themeSettings.logoDarkTreatment,
    isDarkMode,
  );

  const [src, setSrc] = useState(resolved.url ?? fallbackSrc);

  useEffect(() => {
    setSrc(resolved.url ?? fallbackSrc);
  }, [resolved.url, fallbackSrc]);

  // Inline rather than a `dark:` utility class: the treatment is an explicit tenant choice, not
  // a media-query response, and it must not fire for a tenant who chose 'same' or 'custom'.
  return (
    <img
      className={className}
      src={src}
      alt={alt}
      style={resolved.invert ? { filter: LOGO_INVERT_FILTER } : undefined}
      onError={() => setSrc(fallbackSrc)}
    />
  );
}
